import React, { useState, useEffect } from 'react';
import Logo from "../../assets/logo.jpg";
import { getPrimaryClientLogo } from '../../utils/clientLogoService';
import { getDropdownData, getProgramUserRatingsByProgramCode } from '../Queries';

const PrintRatingReport = React.forwardRef(({
    users,
    programCode,
    clientCode,
    reportType = 'initial' // 'initial' or 'final'
}, ref) => {
    
    const [clientLogo, setClientLogo] = useState(null);
    const [ratingMetrics, setRatingMetrics] = useState([]);
    const [userRatingsData, setUserRatingsData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    
    // Fetch client logo
    const fetchClientDetails = async (programCode) => {
        if (!programCode) return;

        try {
            const logoUrl = await getPrimaryClientLogo(programCode);
            setClientLogo(logoUrl);
        } catch (error) {
            console.error("Error fetching client logo:", error);
            setClientLogo(null);
        }
    };

    // Load rating metrics and user ratings
    useEffect(() => {
        const loadData = async () => {
            if (!programCode || !clientCode) return;
            
            setIsLoading(true);
            try {
                // Load rating metrics
                const allDropdownData = await getDropdownData();
                const metrics = allDropdownData.filter(
                    item => item.type === 'program_rating_metrics' && item.client_code === clientCode
                );

                const processedMetrics = metrics.map(m => {
                    const metricsData = m.json_data?.metrics || {};
                    return {
                        id: m.row_id,
                        name: m.name,
                        metrics: metricsData
                    };
                });

                setRatingMetrics(processedMetrics);

                // Load all ratings for the program
                const allRatings = await getProgramUserRatingsByProgramCode(programCode);
                setUserRatingsData(allRatings);

                // Fetch client logo
                await fetchClientDetails(programCode);
            } catch (error) {
                console.error('Error loading data:', error);
            } finally {
                setIsLoading(false);
            }
        };

        loadData();
    }, [programCode, clientCode]);

    // Calculate user's average scores based on report type
    const calculateUserScores = (userEmail) => {
        const userRatings = userRatingsData.filter(rating => rating.user_id === userEmail);
        const data = [];

        ratingMetrics.forEach(metric => {
            if (reportType === 'initial') {
                // Initial Report: Get the actual rating from pre_program_review (not average)
                let initialRating = 0;
                let teamTotalScore = 0;
                let teamCount = 0;

                // Get user's initial rating from pre_program_review
                userRatings.forEach(rating => {
                    // Extract session type from id (format: programCode_sessionType)
                    const sessionType = rating.id?.split('_').slice(1).join('_') || '';
                    
                    if (sessionType === 'pre_program_review') {
                        const ratingJson = rating.rating_json;
                        if (ratingJson && ratingJson[metric.name] && ratingJson[metric.name].rating) {
                            const ratingValue = parseInt(ratingJson[metric.name].rating);
                            if (!isNaN(ratingValue)) {
                                initialRating = ratingValue;
                            }
                        }
                    }
                });

                // Calculate team average from pre_program_review
                userRatingsData.forEach(rating => {
                    // Extract session type from id (format: programCode_sessionType)
                    const sessionType = rating.id?.split('_').slice(1).join('_') || '';
                    
                    if (sessionType === 'pre_program_review') {
                        const ratingJson = rating.rating_json;
                        if (ratingJson && ratingJson[metric.name] && ratingJson[metric.name].rating) {
                            const ratingValue = parseInt(ratingJson[metric.name].rating);
                            if (!isNaN(ratingValue)) {
                                teamTotalScore += ratingValue;
                                teamCount++;
                            }
                        }
                    }
                });

                if (initialRating > 0 || teamCount > 0) {
                    data.push({
                        metric: metric.name,
                        initialAvg: initialRating,
                        teamAvg: teamCount > 0 ? parseFloat((teamTotalScore / teamCount).toFixed(2)) : 0
                    });
                }
            } else {
                // Final Report: Show initial (pre_program_review), final (post_program_review), and team average
                let initialRating = 0;
                let finalRating = 0;
                let teamTotalScore = 0;
                let teamCount = 0;

                // Get user's initial and final ratings
                userRatings.forEach(rating => {
                    const ratingJson = rating.rating_json;
                    if (ratingJson && ratingJson[metric.name] && ratingJson[metric.name].rating) {
                        const ratingValue = parseInt(ratingJson[metric.name].rating);
                        if (!isNaN(ratingValue)) {
                            // Extract session type from id (format: programCode_sessionType)
                            const sessionType = rating.id?.split('_').slice(1).join('_') || '';
                            
                            if (sessionType === 'post_program_review') {
                                finalRating = ratingValue;
                            } else if (sessionType === 'pre_program_review') {
                                initialRating = ratingValue;
                            }
                        }
                    }
                });

                // Calculate team average (all sessions)
                userRatingsData.forEach(rating => {
                    const ratingJson = rating.rating_json;
                    if (ratingJson && ratingJson[metric.name] && ratingJson[metric.name].rating) {
                        const ratingValue = parseInt(ratingJson[metric.name].rating);
                        if (!isNaN(ratingValue)) {
                            teamTotalScore += ratingValue;
                            teamCount++;
                        }
                    }
                });

                if (initialRating > 0 || finalRating > 0 || teamCount > 0) {
                    data.push({
                        metric: metric.name,
                        initialAvg: initialRating,
                        finalAvg: finalRating,
                        teamAvg: teamCount > 0 ? parseFloat((teamTotalScore / teamCount).toFixed(2)) : 0
                    });
                }
            }
        });

        return data;
    };

    // Get description from user's rating data
    const getUserDescription = (userEmail) => {
        const userRatings = userRatingsData.filter(rating => rating.user_id === userEmail);
        
        if (reportType === 'initial') {
            // Get description from pre_program_review
            const preProgram = userRatings.find(rating => {
                const sessionType = rating.id?.split('_').slice(1).join('_') || '';
                return sessionType === 'pre_program_review';
            });
            return preProgram?.rating_json?._description || 'No description available.';
        } else {
            // Get descriptions from both pre and post program
            const preProgram = userRatings.find(rating => {
                const sessionType = rating.id?.split('_').slice(1).join('_') || '';
                return sessionType === 'pre_program_review';
            });
            const postProgram = userRatings.find(rating => {
                const sessionType = rating.id?.split('_').slice(1).join('_') || '';
                return sessionType === 'post_program_review';
            });
            
            const preDesc = preProgram?.rating_json?._description || '';
            const postDesc = postProgram?.rating_json?._description || '';
            
            if (preDesc && postDesc) {
                return `Initial: ${preDesc}\n\nFinal: ${postDesc}`;
            } else if (preDesc) {
                return `Initial: ${preDesc}`;
            } else if (postDesc) {
                return `Final: ${postDesc}`;
            } else {
                return 'No description available.';
            }
        }
    };

    // Chart to SVG conversion for horizontal bar chart
    const horizontalChartToSVG = (chartData) => {
        const containerWidth = 700;
        const legendHeight = 50;
        const barsPerMetric = reportType === 'initial' ? 2 : 3;
        const chartHeight = chartData.length * (barsPerMetric === 2 ? 50 : 65);
        const containerHeight = chartHeight + legendHeight + 60;
        const barHeight = 15;
        const leftMargin = 180;

        const legendItems = reportType === 'initial' 
            ? [
                { color: '#5DADE2', label: 'Initial', x: leftMargin + 10 },
                { color: '#1A237E', label: 'Team Average', x: leftMargin + 100 }
              ]
            : [
                { color: '#5DADE2', label: 'Initial', x: leftMargin + 10 },
                { color: '#16A085', label: 'Final', x: leftMargin + 100 },
                { color: '#1A237E', label: 'Team Average', x: leftMargin + 180 }
              ];

        return `
            <svg width="${containerWidth}" height="${containerHeight}" style="background: white;">
                <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                        <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#f0f0f0" stroke-width="1"/>
                    </pattern>
                </defs>
                
                <!-- Legend at top -->
                ${legendItems.map(item => `
                    <rect x="${item.x}" y="10" width="15" height="10" fill="${item.color}" rx="2"/>
                    <text x="${item.x + 20}" y="18" font-size="10" fill="#333" font-family="Arial">${item.label}</text>
                `).join('')}
                
                <rect x="${leftMargin}" y="${legendHeight}" width="${containerWidth - leftMargin - 20}" height="${chartHeight + 40}" fill="url(#grid)" />
                
                <!-- Y-axis labels (metrics) -->
                ${chartData.map((item, index) => {
                    const yOffset = barsPerMetric === 2 ? 50 : 65;
                    const y = index * yOffset + legendHeight + (barsPerMetric === 2 ? 30 : 35);
                    return `
                        <text x="${leftMargin - 10}" y="${y}" text-anchor="end" font-size="10" fill="#333" font-family="Arial">${item.metric.length > 25 ? item.metric.substring(0, 25) + '...' : item.metric}</text>
                    `;
                }).join('')}
                
                <!-- Bars -->
                ${chartData.map((item, index) => {
                    const yOffset = barsPerMetric === 2 ? 50 : 65;
                    const y = index * yOffset + legendHeight + (barsPerMetric === 2 ? 15 : 20);
                    
                    if (reportType === 'initial') {
                        const initialBarWidth = (item.initialAvg / 5) * (containerWidth - leftMargin - 40);
                        const teamBarWidth = (item.teamAvg / 5) * (containerWidth - leftMargin - 40);
                        
                        return `
                            <!-- Initial Bar -->
                            <rect x="${leftMargin + 10}" y="${y}" width="${initialBarWidth}" height="${barHeight}" fill="#5DADE2" rx="4"/>
                            <text x="${leftMargin + initialBarWidth + 15}" y="${y + barHeight/2 + 4}" font-size="10" fill="#333" font-weight="bold">${item.initialAvg.toFixed(1)}</text>
                            
                            <!-- Team Average Bar -->
                            <rect x="${leftMargin + 10}" y="${y + barHeight + 3}" width="${teamBarWidth}" height="${barHeight}" fill="#1A237E" rx="4"/>
                            <text x="${leftMargin + teamBarWidth + 15}" y="${y + barHeight + barHeight/2 + 7}" font-size="10" fill="#333" font-weight="bold">${item.teamAvg.toFixed(1)}</text>
                        `;
                    } else {
                        const initialBarWidth = (item.initialAvg / 5) * (containerWidth - leftMargin - 40);
                        const finalBarWidth = (item.finalAvg / 5) * (containerWidth - leftMargin - 40);
                        const teamBarWidth = (item.teamAvg / 5) * (containerWidth - leftMargin - 40);
                        
                        return `
                            <!-- Initial Bar -->
                            <rect x="${leftMargin + 10}" y="${y}" width="${initialBarWidth}" height="${barHeight}" fill="#5DADE2" rx="4"/>
                            <text x="${leftMargin + initialBarWidth + 15}" y="${y + barHeight/2 + 4}" font-size="10" fill="#333" font-weight="bold">${item.initialAvg.toFixed(1)}</text>
                            
                            <!-- Final Bar -->
                            <rect x="${leftMargin + 10}" y="${y + barHeight + 3}" width="${finalBarWidth}" height="${barHeight}" fill="#16A085" rx="4"/>
                            <text x="${leftMargin + finalBarWidth + 15}" y="${y + barHeight + barHeight/2 + 7}" font-size="10" fill="#333" font-weight="bold">${item.finalAvg.toFixed(1)}</text>
                            
                            <!-- Team Average Bar -->
                            <rect x="${leftMargin + 10}" y="${y + barHeight * 2 + 6}" width="${teamBarWidth}" height="${barHeight}" fill="#1A237E" rx="4"/>
                            <text x="${leftMargin + teamBarWidth + 15}" y="${y + barHeight * 2 + barHeight/2 + 10}" font-size="10" fill="#333" font-weight="bold">${item.teamAvg.toFixed(1)}</text>
                        `;
                    }
                }).join('')}
                
                <!-- X-axis -->
                <line x1="${leftMargin + 10}" y1="${chartHeight + legendHeight + 40}" x2="${containerWidth - 20}" y2="${chartHeight + legendHeight + 40}" stroke="#666" stroke-width="1"/>
                ${[0, 1, 2, 3, 4, 5].map(tick => {
                    const x = leftMargin + 10 + (tick / 5) * (containerWidth - leftMargin - 40);
                    return `
                        <line x1="${x}" y1="${chartHeight + legendHeight + 40}" x2="${x}" y2="${chartHeight + legendHeight + 45}" stroke="#666" stroke-width="1"/>
                        <text x="${x}" y="${chartHeight + legendHeight + 55}" text-anchor="middle" font-size="10" fill="#666">${tick}</text>
                    `;
                }).join('')}
            </svg>
        `;
    };

    // Header component
    const PrintHeader = () => (
        <div className="print-header">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', position: 'relative' }}>
                <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto' }}>
                    <img src={Logo} alt="Logo" style={{ height: '30px' }} />
                </div>
                
                <div style={{ 
                    position: 'absolute', 
                    left: '50%', 
                    transform: 'translateX(-50%)', 
                    textAlign: 'center',
                    zIndex: 1,
                    width: '80%',
                    maxWidth: '600px'
                }}>
                    <div style={{ fontSize: '14px', fontWeight: 'bold', color: '#0a0068' }}>
                        {reportType === 'initial' ? 'Initial Report' : 'Final Report'}
                    </div>
                </div>
                
                <div style={{ display: 'flex', alignItems: 'center', flex: '0 0 auto', justifyContent: 'flex-end' }}>
                    {clientLogo && (
                        <img 
                            src={clientLogo} 
                            alt="Client Logo" 
                            style={{ 
                                height: '35px', 
                                maxWidth: '120px', 
                                objectFit: 'contain'
                            }} 
                        />
                    )}
                </div>
            </div>
            <hr style={{ 
                border: 'none', 
                borderTop: '2px solid #0a0068', 
                margin: '5px 0', 
                width: '100%' 
            }} />
        </div>
    );

    // Footer component
    const PrintFooter = () => (
        <div className="print-footer">
            <div style={{ fontSize: '9px', color: '#666', lineHeight: '1.5', textAlign: 'center', paddingTop: '10px' }}>
                <div style={{ marginBottom: '3px' }}>
                    Copyright © Talent Sculptors. All Rights Reserved. Confidential.
                </div>
                <div>
                    This document may be shared or distributed at the discretion of the recipient, in line with their organization's policies and confidentiality requirements.
                </div>
            </div>
        </div>
    );

    if (isLoading) {
        return (
            <div ref={ref} style={{ padding: '20px', textAlign: 'center' }}>
                <h2>Loading report data...</h2>
            </div>
        );
    }

    if (!users || users.length === 0) {
        return (
            <div ref={ref} style={{ padding: '20px', textAlign: 'center' }}>
                <h2>No User Data Available</h2>
            </div>
        );
    }

    return (
        <div ref={ref} className="print-wrapper" style={{ fontFamily: 'Arial, sans-serif', backgroundColor: 'white', color: 'black', lineHeight: '1.4' }}>
            <style>
                {`
                    @media print {
                        @page {
                            size: A4;
                            margin: 0;
                        }
                        body {
                            margin: 0;
                            padding: 0;
                        }
                        .print-wrapper {
                            margin: 0;
                            padding: 10px 20px;
                        }
                        .page-break {
                            page-break-after: always;
                            page-break-inside: avoid;
                            padding-top: 20px;
                        }
                        .page-break:first-child {
                            padding-top: 0;
                        }
                        .print-header {
                            margin-bottom: 10px;
                        }
                        .print-footer {
                            position: fixed;
                            bottom: 0;
                            left: 0;
                            right: 0;
                            padding: 0 20px 10px 20px;
                            background: white;
                        }
                        .print-content {
                            padding: 0 20px;
                            padding-bottom: 80px;
                        }
                    }
                `}
            </style>
            {/* First Page: Rating Metrics - Common for all users */}
            <div className="page-break" style={{ pageBreakAfter: 'always' }}>
                <PrintHeader />
                
                <div className="print-content" style={{ minHeight: '900px' }}>
                    {/* Rating Metrics Table Section */}
                    <section style={{ marginBottom: '15px', marginTop: '5px' }}>
                        <h2 style={{ color: '#0a0068', marginBottom: '8px', marginTop: '0', fontSize: '16px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px' }}>
                            Rating Metrics
                        </h2>
                        <div style={{ 
                            backgroundColor: 'white', 
                            borderRadius: '8px', 
                            border: '1px solid #e9ecef',
                            overflow: 'hidden'
                        }}>
                            <table style={{ 
                                width: '100%', 
                                borderCollapse: 'collapse',
                                fontSize: '9px'
                            }}>
                                <thead>
                                    <tr style={{ backgroundColor: '#f8f9fa' }}>
                                        <th style={{ 
                                            padding: '8px', 
                                            textAlign: 'left', 
                                            borderBottom: '2px solid #dee2e6',
                                            fontWeight: '600',
                                            width: '4%',
                                            color: '#0a0068',
                                            fontSize: '9px'
                                        }}>S.No</th>
                                        <th style={{ 
                                            padding: '8px', 
                                            textAlign: 'left', 
                                            borderBottom: '2px solid #dee2e6',
                                            fontWeight: '600',
                                            width: '14%',
                                            color: '#0a0068',
                                            fontSize: '9px'
                                        }}>Metric Name</th>
                                        <th style={{ 
                                            padding: '8px', 
                                            textAlign: 'left', 
                                            borderBottom: '2px solid #dee2e6',
                                            fontWeight: '600',
                                            width: '16.4%',
                                            color: '#0a0068',
                                            fontSize: '9px'
                                        }}>5 - Excellent</th>
                                        <th style={{ 
                                            padding: '8px', 
                                            textAlign: 'left', 
                                            borderBottom: '2px solid #dee2e6',
                                            fontWeight: '600',
                                            width: '16.4%',
                                            color: '#0a0068',
                                            fontSize: '9px'
                                        }}>4 - Good</th>
                                        <th style={{ 
                                            padding: '8px', 
                                            textAlign: 'left', 
                                            borderBottom: '2px solid #dee2e6',
                                            fontWeight: '600',
                                            width: '16.4%',
                                            color: '#0a0068',
                                            fontSize: '9px'
                                        }}>3 - Average</th>
                                        <th style={{ 
                                            padding: '8px', 
                                            textAlign: 'left', 
                                            borderBottom: '2px solid #dee2e6',
                                            fontWeight: '600',
                                            width: '16.4%',
                                            color: '#0a0068',
                                            fontSize: '9px'
                                        }}>2 - Below Average</th>
                                        <th style={{ 
                                            padding: '8px', 
                                            textAlign: 'left', 
                                            borderBottom: '2px solid #dee2e6',
                                            fontWeight: '600',
                                            width: '16.4%',
                                            color: '#0a0068',
                                            fontSize: '9px'
                                        }}>1 - Poor</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {ratingMetrics.map((metric, index) => (
                                        <tr key={metric.id} style={{ 
                                            backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8f9fa' 
                                        }}>
                                            <td style={{ 
                                                padding: '6px 8px', 
                                                borderBottom: '1px solid #dee2e6',
                                                fontWeight: '500',
                                                verticalAlign: 'top',
                                                fontSize: '9px'
                                            }}>{index + 1}</td>
                                            <td style={{ 
                                                padding: '6px 8px', 
                                                borderBottom: '1px solid #dee2e6',
                                                fontWeight: '600',
                                                color: '#0a0068',
                                                verticalAlign: 'top',
                                                fontSize: '9px'
                                            }}>{metric.name}</td>
                                            <td style={{ 
                                                padding: '6px 8px', 
                                                borderBottom: '1px solid #dee2e6',
                                                color: '#333',
                                                lineHeight: '1.4',
                                                verticalAlign: 'top',
                                                fontSize: '9px'
                                            }}>{metric.metrics?.['5-Excellent']?.description || ''}</td>
                                            <td style={{ 
                                                padding: '6px 8px', 
                                                borderBottom: '1px solid #dee2e6',
                                                color: '#333',
                                                lineHeight: '1.4',
                                                verticalAlign: 'top',
                                                fontSize: '9px'
                                            }}>{metric.metrics?.['4-Good']?.description || ''}</td>
                                            <td style={{ 
                                                padding: '6px 8px', 
                                                borderBottom: '1px solid #dee2e6',
                                                color: '#333',
                                                lineHeight: '1.4',
                                                verticalAlign: 'top',
                                                fontSize: '9px'
                                            }}>{metric.metrics?.['3-Average']?.description || ''}</td>
                                            <td style={{ 
                                                padding: '6px 8px', 
                                                borderBottom: '1px solid #dee2e6',
                                                color: '#333',
                                                lineHeight: '1.4',
                                                verticalAlign: 'top',
                                                fontSize: '9px'
                                            }}>{metric.metrics?.['2-Below Average']?.description || ''}</td>
                                            <td style={{ 
                                                padding: '6px 8px', 
                                                borderBottom: '1px solid #dee2e6',
                                                color: '#333',
                                                lineHeight: '1.4',
                                                verticalAlign: 'top',
                                                fontSize: '9px'
                                            }}>{metric.metrics?.['1-Poor']?.description || ''}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </section>
                </div>
                
                <PrintFooter />
            </div>

            {/* Generate one page per user */}
            {users.map((user, userIndex) => {
                const userScores = calculateUserScores(user.email);
                const userDescription = getUserDescription(user.email);
                
                if (userScores.length === 0) return null;

                return (
                    <div key={user.email} className="page-break" style={{ pageBreakAfter: userIndex < users.length - 1 ? 'always' : 'auto' }}>
                        <PrintHeader />
                        
                        <div className="print-content" style={{ minHeight: '900px' }}>
                            {/* User Information Section */}
                            <section style={{ marginBottom: '25px' }}>
                                <h2 style={{ color: '#0a0068', marginBottom: '15px', fontSize: '16px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px' }}>
                                    Participant Information
                                </h2>
                                <div style={{ 
                                    padding: '20px', 
                                    backgroundColor: '#f9f9f9', 
                                    borderRadius: '8px',
                                    border: '1px solid #e9ecef'
                                }}>
                                    <div style={{ display: 'flex', alignItems: 'center', marginBottom: '15px' }}>
                                        <div style={{
                                            width: '60px', 
                                            height: '60px', 
                                            borderRadius: '50%', 
                                            backgroundColor: '#0a0068',
                                            color: 'white', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'center',
                                            fontWeight: 'bold', 
                                            fontSize: '24px', 
                                            marginRight: '20px'
                                        }}>
                                            {user.name?.charAt(0) || 'U'}
                                        </div>
                                        <div>
                                            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#333', marginBottom: '5px' }}>
                                                {user.name}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#666' }}>
                                                {user.email}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Description */}
                                    <div style={{ marginTop: '15px', paddingTop: '15px', borderTop: '1px solid #e0e0e0' }}>
                                        <div style={{ 
                                            fontSize: '12px', 
                                            color: '#333', 
                                            lineHeight: '1.6',
                                            whiteSpace: 'pre-wrap'
                                        }}>
                                            {userDescription}
                                        </div>
                                    </div>
                                </div>
                            </section>

                            {/* Performance Chart Section */}
                            <section style={{ marginBottom: '25px' }}>
                                <h2 style={{ color: '#0a0068', marginBottom: '15px', fontSize: '16px', borderBottom: '2px solid #f0f0f0', paddingBottom: '5px' }}>
                                    Performance Metrics
                                </h2>
                                <div style={{ 
                                    backgroundColor: 'white', 
                                    padding: '20px', 
                                    borderRadius: '8px', 
                                    border: '1px solid #e9ecef'
                                }}>
                                    {reportType === 'final' && (
                                        <div style={{ fontSize: '11px', color: '#666', marginBottom: '15px', textAlign: 'center' }}>
                                            Average scores across all completed sessions (Scale: 0-5)
                                        </div>
                                    )}
                                    <div dangerouslySetInnerHTML={{ __html: horizontalChartToSVG(userScores) }} />
                                </div>
                            </section>
                        </div>
                        
                        <PrintFooter />
                    </div>
                );
            })}
        </div>
    );
});

export default PrintRatingReport;
